import { Focusable } from '@ariakit/react'
import { format } from 'date-fns'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import pluralize from 'pluralize'
import { forwardRef, useCallback, useMemo, useState } from 'react'

import {
  Dialog,
  ExpandableText,
  MediaThumbnail,
  SelectMark,
  Star,
  StarRating,
  ViewportSizes,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { useModalState } from '../../../components/modals/useModalState'
import * as Review from '../../../components/reviews/Review.styles'
import { ReviewDetailsModal } from '../../../components/reviews/ReviewDetailsModal/ReviewDetailsModal'
import * as ReviewModal from '../../../components/reviews/ReviewDetailsModal/ReviewDetailsModal.styles'
import {
  ReviewMediaTile,
  ReviewPhotosSection,
} from '../../../components/reviews/ReviewPhotosSection/ReviewPhotosSection'
import {
  type ReviewMedia,
  ReviewsSwiperMediaSlider,
} from '../../../components/reviews/ReviewsSwiperMediaSlider/ReviewsSwiperMediaSlider'
import { trackEvent } from '../../../lib/analytics'
import { fieldsByFieldId } from '../../../lib/reviews/constants'
import {
  getFieldValue,
  getTranslatedValueFromReviewsFilter,
  isReviewMediaImage,
  isReviewsTranslatedFilterWithValuesKey,
} from '../../../lib/reviews/utils'
import { useProductReviewsHref, useProductWriteReviewHref } from '../../../lib/routing'
import { isBrowser } from '../../../lib/utils'
import { ProductReview, ProductReviewSet } from '../../../types/graphql'

import {
  ButtonText,
  ButtonWrapper,
  ButtonWrapperFooter,
  ButtonWrapperMobile,
  PaddedItem,
  ProductDetailsCopy,
  ReviewAverageScore,
  ReviewBackground,
  ReviewButton,
  ReviewCountText,
  ReviewCountTextContainerBody,
  ReviewHeader,
  ReviewItem,
  ReviewPhotosWrapper,
  ReviewRatingsContainer,
  ReviewStarIconContainer,
  ReviewStars,
  ReviewTitle,
  ReviewTitleButtonWrapper,
  ReviewWrapper,
} from './styles'

export interface IProps {
  handle: string
  productReviews: ProductReviewSet | undefined
}

export const ReviewsBody = forwardRef<HTMLDivElement, IProps>(({ productReviews, handle }, ref) => {
  const {
    detailsSection: {
      reviewBaseOn,
      reviewPhotosTitle,
      reviewsCountNoun,
      reviewsTitle,
      reviewsEmptyTitle,
      seeAllReviewsCTA,
      writeReviewCTA,
    },
    magnolia: {
      reviewsPage: {
        readMore,
        readLess,
        filters: { filter: filterFixtures },
      },
      navigation,
    },
  } = useTranslation()
  // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
  const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined
  const isSmallViewport = useIsViewport(ViewportSizes['small'])
  const reviewsMagnoliaViewPath = useProductReviewsHref(handle, {
    plain: plainQueryParamValue,
  })
  const reviewsMagnoliaWritePath = useProductWriteReviewHref(handle, {
    plain: plainQueryParamValue,
  })
  const {
    isOpen: isReviewDetailsModalOpen,
    openModal: openReviewDetailsModal,
    closeModal: closeReviewDetailsModal,
  } = useModalState()
  const [selectedReview, setSelectedReview] = useState<ProductReview | undefined>()
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | undefined>()

  const onThumbnailClick = useCallback(
    (reviewIndex: number, mediaIndex?: number) => {
      setSelectedReview(productReviews?.reviews[reviewIndex])
      setSelectedMediaIndex(mediaIndex)
      openReviewDetailsModal()
    },
    [openReviewDetailsModal, productReviews?.reviews]
  )

  const swiperAssets: ReviewMedia[] | undefined = useMemo(() => {
    if (selectedReview && selectedReview.imageUrls) {
      return selectedReview.imageUrls.map(url => ({
        src: url,
        type: isReviewMediaImage(url) ? 'image' : 'video',
      }))
    }
    return undefined
  }, [selectedReview])

  const aggregateReviewMedia = useMemo(() => {
    const output: ReviewMediaTile[] = []
    productReviews?.reviews.forEach(({ imageUrls }, reviewIndex) => {
      if (imageUrls) {
        output.push(
          ...imageUrls.map<ReviewMediaTile>((e, mediaIndex) => ({
            src: e,
            type: isReviewMediaImage(e) ? 'image' : 'video',
            reviewIndex,
            mediaIndex,
          }))
        )
      }
    })
    return output
  }, [productReviews?.reviews])

  if (!productReviews || !isBrowser()) return null

  const reviewTitleText: string =
    productReviews?.totalReviews !== null
      ? `${productReviews.totalReviews.toLocaleString()} ${pluralize(
          reviewsCountNoun,
          productReviews.totalReviews
        )}`
      : 'Reviews'

  const displayRating = (productReviews?.averageScore || 0).toFixed(2)

  const tooltip = `Rating of this product is ${displayRating} out of 5.`

  const NoReviewComponent = (
    <ReviewWrapper ref={ref}>
      <ReviewTitle style={{ margin: '0 0 16px' }} asTag='h2'>
        {reviewsEmptyTitle}
      </ReviewTitle>
      <ReviewStarIconContainer style={{ marginBottom: '40px' }}>
        {Array.from({ length: 5 }, (_item, index) => (
          <Star key={`${index}-3`} height={28} width={28} />
        ))}
      </ReviewStarIconContainer>
      <Link aria-label={writeReviewCTA} href={reviewsMagnoliaWritePath} rel='noopener noreferrer'>
        <ButtonWrapper>
          <ReviewButton>{writeReviewCTA}</ReviewButton>
        </ButtonWrapper>
      </Link>
    </ReviewWrapper>
  )

  const ReviewButtonComponent = () => {
    return (
      <Link aria-label={seeAllReviewsCTA} href={reviewsMagnoliaViewPath} rel='noopener noreferrer'>
        <ButtonWrapper>
          <ReviewButton {...trackEvent({ category: 'product details', action: 'see all reviews' })}>
            <ButtonText>{seeAllReviewsCTA}</ButtonText>
          </ReviewButton>
        </ButtonWrapper>
      </Link>
    )
  }

  const hasReviews = productReviews.reviews.length > 0
  if (!hasReviews) return NoReviewComponent

  return (
    <Dialog.Provider>
      <ReviewBackground>
        <PaddedItem {...{ ref }} id='reviews-padded-item' />
        <ReviewWrapper>
          <ReviewHeader>
            <ReviewTitle asTag='h2'>{reviewsTitle}</ReviewTitle>
            <ReviewTitleButtonWrapper>
              <ReviewButtonComponent />
            </ReviewTitleButtonWrapper>
          </ReviewHeader>
          <ReviewRatingsContainer>
            <ReviewAverageScore>{productReviews.averageScore?.toFixed(1)}/5</ReviewAverageScore>
            <ReviewStarIconContainer>
              <ReviewStars
                aria-label={tooltip}
                rating={productReviews?.averageScore || 5}
                title={tooltip}
                isLarge
              />
            </ReviewStarIconContainer>
            <ReviewCountTextContainerBody>
              <ProductDetailsCopy>
                <ReviewCountText>
                  {reviewBaseOn} {reviewTitleText}
                </ReviewCountText>
              </ProductDetailsCopy>
            </ReviewCountTextContainerBody>
          </ReviewRatingsContainer>
          {aggregateReviewMedia.length > 0 ? (
            <ReviewPhotosWrapper>
              <ReviewPhotosSection
                tiles={aggregateReviewMedia}
                tileWidthMobile={104}
                tileWidthDesktop={150.5}
                tileGapMobile={8}
                tileGapDesktop={10.5}
                titleText={reviewPhotosTitle}
                renderTile={(tile: ReviewMediaTile, index: number) => (
                  <ReviewModal.ThumbnailTarget
                    onClick={() => onThumbnailClick(tile.reviewIndex, tile.mediaIndex)}
                    render={
                      <Focusable>
                        <MediaThumbnail
                          src={tile.src}
                          isVideo={tile.type === 'video'}
                          loading='lazy'
                          alt={`Review Media Thumbnail ${index + 1}`}
                          aspectRatios={{ sm: 0.75, md: 0.75 }}
                          widths={{ unit: 'px', sm: 96, md: 140 }}
                        />
                      </Focusable>
                    }
                  />
                )}
              />
            </ReviewPhotosWrapper>
          ) : null}
          <ButtonWrapperMobile>
            <ReviewButtonComponent />
          </ButtonWrapperMobile>
          {productReviews.reviews.map((review, reviewIndex) => (
            <ReviewItem key={`${reviewIndex}-${review.reviewer}`}>
              <Review.Card isPDP={true}>
                <Review.ReviewContent>
                  <Review.Header>
                    <StarRating rating={review.score} starSize='16px' />
                    {review.createdAt ? (
                      <Review.CreatedAtDate asTag='span'>
                        {format(review.createdAt, 'MMMM d, yyyy')}
                      </Review.CreatedAtDate>
                    ) : null}
                  </Review.Header>
                  <Review.Title asTag='h2'>{review.title}</Review.Title>
                  <Review.BodyText>
                    <ExpandableText
                      content={review.content}
                      truncateLength={260}
                      readMoreText={readMore}
                      readLessText={readLess}
                    />
                  </Review.BodyText>
                  {review.imageUrls?.length ? (
                    <Review.ThumbnailsSection>
                      {review.imageUrls.map((url, urlIndex) => {
                        if (isSmallViewport && urlIndex > 1) return null
                        const isImage =
                          /\.(jpg|jpeg|png|gif|bmp|webp|svg|tiff?|ico|apng|avif|heif|heic)(\?[^ ]*)?$/.test(
                            url
                          )
                        return (
                          <Review.ThumbnailWrapper key={url}>
                            <ReviewModal.ThumbnailTarget
                              onClick={() => onThumbnailClick(reviewIndex, urlIndex)}
                              render={
                                <Focusable>
                                  <MediaThumbnail
                                    src={url}
                                    alt={`Review Media Thumbnail ${urlIndex + 1}`}
                                    isVideo={!isImage}
                                    aspectRatios={{ sm: 0.75, md: 0.75 }}
                                    loading='default'
                                    widths={{ unit: 'px', sm: 96, md: 66 }}
                                    {...trackEvent({
                                      category: 'review highlights',
                                      action: 'click tile',
                                      value: 'image',
                                    })}
                                  />
                                </Focusable>
                              }
                            />
                          </Review.ThumbnailWrapper>
                        )
                      })}
                      {review.imageUrls.length - 2 > 0 ? (
                        <Review.ThumbnailsOverflow>
                          {`+ ${review.imageUrls.length - 2}`}
                        </Review.ThumbnailsOverflow>
                      ) : null}
                    </Review.ThumbnailsSection>
                  ) : null}
                </Review.ReviewContent>
                {review.customFields && review.customFields.length > 0 ? (
                  <Review.PurchaseDetailsWrapper hasContent>
                    <Review.PurchaseDetails>
                      <Review.ReviewerName>{review.reviewer}</Review.ReviewerName>
                      <Review.DetailsList>
                        {review.customFields
                          .map(({ key, value }) => {
                            const field = fieldsByFieldId[key!]
                            return {
                              id: field?.fieldId,
                              name: field?.name,
                              value: field ? getFieldValue(field, value!) : undefined,
                            }
                          })
                          .map(({ id, name, value }) => {
                            if (!id || !name || !value) return null

                            const isNameValidKey = isReviewsTranslatedFilterWithValuesKey(name)

                            const localizedName = isNameValidKey
                              ? filterFixtures[name].title
                              : undefined

                            const localizedValue = isNameValidKey
                              ? getTranslatedValueFromReviewsFilter({
                                  valueKey: value,
                                  filter: filterFixtures[name],
                                })
                              : undefined

                            return (
                              <Review.DetailsItem key={id}>
                                <b>{localizedName ?? name}:</b> {localizedValue ?? value}
                              </Review.DetailsItem>
                            )
                          })}
                      </Review.DetailsList>
                    </Review.PurchaseDetails>
                  </Review.PurchaseDetailsWrapper>
                ) : null}
              </Review.Card>
            </ReviewItem>
          ))}
          <ButtonWrapperFooter>
            <ReviewButtonComponent />
          </ButtonWrapperFooter>
        </ReviewWrapper>
        <ReviewDetailsModal isOpen={isReviewDetailsModalOpen} onClose={closeReviewDetailsModal}>
          <ReviewModal.CloseButtonWrapper displayOnMobile={true}>
            <ReviewModal.CloseButton
              onClick={closeReviewDetailsModal}
              aria-label={navigation.closeButtonLabel}
              {...trackEvent({ category: 'reviews modal', action: 'close' })}
            >
              <SelectMark width='16px' height='16px' strokeWidth={0.75} />
            </ReviewModal.CloseButton>
          </ReviewModal.CloseButtonWrapper>
          <ReviewModal.SwiperWrapper>
            {swiperAssets && swiperAssets.length > 0 ? (
              <ReviewsSwiperMediaSlider
                sliderAssets={swiperAssets}
                initialSlideIndex={selectedMediaIndex}
              />
            ) : null}
          </ReviewModal.SwiperWrapper>
          <ReviewModal.ReviewWrapper>
            <ReviewModal.CloseButtonWrapper displayOnMobile={false}>
              <ReviewModal.CloseButton
                onClick={closeReviewDetailsModal}
                aria-label={navigation.closeButtonLabel}
                {...trackEvent({ category: 'reviews modal', action: 'close' })}
              >
                <SelectMark width='16px' height='16px' strokeWidth={0.75} />
              </ReviewModal.CloseButton>
            </ReviewModal.CloseButtonWrapper>
            {selectedReview ? (
              <ReviewModal.ReviewContent>
                <ReviewModal.Header>
                  <StarRating rating={selectedReview.score} starSize='16px' />
                  {selectedReview.createdAt ? (
                    <ReviewModal.CreatedAtDate asTag='span'>
                      {format(selectedReview.createdAt, 'MMMM d, yyyy')}
                    </ReviewModal.CreatedAtDate>
                  ) : null}
                </ReviewModal.Header>
                <ReviewModal.Title asTag='h2'>{selectedReview.title}</ReviewModal.Title>
                <ReviewModal.BodyText>
                  <ExpandableText
                    content={selectedReview.content}
                    truncateLength={260}
                    readMoreText={readMore}
                    readLessText={readLess}
                  />
                </ReviewModal.BodyText>
                {selectedReview.customFields && selectedReview.customFields.length > 0 ? (
                  <ReviewModal.PurchaseDetailsWrapper hasContent>
                    <ReviewModal.PurchaseDetails>
                      <ReviewModal.ReviewerName>{selectedReview.reviewer}</ReviewModal.ReviewerName>
                      <ReviewModal.DetailsList>
                        {selectedReview.customFields
                          .map(({ key, value }) => {
                            const field = fieldsByFieldId[key!]
                            return {
                              field,
                              value: field ? getFieldValue(field, value!) : undefined,
                            }
                          })
                          .filter(({ field, value }) => field && value)
                          .map(({ field, value }) => (
                            <ReviewModal.DetailsItem key={field!.fieldId}>
                              <b>{field!.name}:</b> {value}
                            </ReviewModal.DetailsItem>
                          ))}
                      </ReviewModal.DetailsList>
                    </ReviewModal.PurchaseDetails>
                  </ReviewModal.PurchaseDetailsWrapper>
                ) : null}
              </ReviewModal.ReviewContent>
            ) : null}
          </ReviewModal.ReviewWrapper>
        </ReviewDetailsModal>
      </ReviewBackground>
    </Dialog.Provider>
  )
})
